import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import type { BestTimeSlot } from '@/hooks/analytics/useInsights';
import { useAuth } from '@/hooks/use-auth';
import { useBrandKits } from '@/hooks/useBrandKits';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

export type { BestTimeSlot };

// Validated against the `get_best_time` RPC's RETURNS TABLE shape (migration
// 20260719160000_best_time_require_slot_support.sql) so a column
// rename/drop surfaces as a query error instead of silently degrading to
// SAFE_DEFAULTS for every caller.
const BestTimeSlotRowSchema = z.object({
  dow: z.string(),
  hour: z.number(),
  label: z.string(),
});

const BestTimeRowSchema = z.object({
  kind: z.string().nullable().optional(),
  finding: z.string().nullable().optional(),
  supporting: z.string().nullable().optional(),
  n: z.number().nullable().optional(),
  confidence: z.string().nullable().optional(),
  gated: z.boolean().nullable().optional(),
  cta: z.string().nullable().optional(),
  tier: z.number().nullable().optional(),
  tier_label: z.string().nullable().optional(),
  top_slots: z
    .union([z.array(BestTimeSlotRowSchema), z.record(z.string(), BestTimeSlotRowSchema)])
    .nullable()
    .optional(),
});

const BestTimeRowsSchema = z.array(BestTimeRowSchema);

/**
 * The full result shape returned by `useBestTimeSlots`.
 *
 * Fields mirror the `get_best_time` RPC return columns:
 *   tier SMALLINT (0=benchmark, 1=follower-online, 2=personal-history)
 *   tier_label TEXT — human-readable tier provenance string from the RPC
 *   n INTEGER — number of posts contributing to this insight
 *   confidence TEXT — 'low' | 'medium' | 'high'
 *   gated BOOLEAN — true when data is insufficient and the result falls back
 *   top_slots JSONB — [{dow, hour, label}] in the user's local timezone
 */
export interface BestTimeSlotsResult {
  slots: BestTimeSlot[];
  tier: 0 | 1 | 2;
  tierLabel: string;
  n: number;
  confidence: string;
  gated: boolean;
  isLoading: boolean;
}

/**
 * Parse `top_slots` from the RPC row. Mirrors the mapRow logic in useInsights.ts
 * (Array branch + object-values fallback).
 */
function parseTopSlots(rawSlots: unknown): BestTimeSlot[] {
  if (Array.isArray(rawSlots)) return rawSlots as BestTimeSlot[];
  if (rawSlots != null && typeof rawSlots === 'object')
    return Object.values(rawSlots as Record<string, BestTimeSlot>);
  return [];
}

// Slot→time-string conversion lives in `useBulkScheduling.ts` (`bestSlotsToTimeStrings`,
// dedup + rank-preserving, unit-tested) — the single source of truth consumed by
// EnhancedBulkSchedulingCalendar. Not duplicated here to avoid two divergent impls.

const SAFE_DEFAULTS: Omit<BestTimeSlotsResult, 'isLoading'> = {
  slots: [],
  tier: 0,
  tierLabel: '',
  n: 0,
  confidence: 'low',
  gated: false,
};

/**
 * React Query hook that wraps the `get_best_time` RPC.
 *
 * - `enabled: !!user` — only fires when an authenticated user is present.
 * - `staleTime: 5 * 60 * 1000` — caches the result for 5 minutes per session.
 * - `queryKey: ['best-time-slots', userId, platform]` — platform-scoped cache.
 * - SECURITY INVOKER: Tier-1/2 personalization only resolves when `auth.uid() = p_user_id`.
 *   A service-role or unauthenticated context always returns Tier-0 benchmark — never
 *   another user's data.
 *
 * @param platform - Social platform slug (default: `'instagram'`).
 * @returns `BestTimeSlotsResult` with `{ slots, tier, tierLabel, n, confidence, gated, isLoading }`.
 */
export function useBestTimeSlots(
  platform: string = 'instagram',
  /**
   * Scope to this workspace instead of the active one — a post's own
   * brand_kit_id, so a post from another workspace is not timed by the
   * active workspace's history.
   */
  brandKitId?: string | null,
): BestTimeSlotsResult {
  const { user } = useAuth();
  const { activeKit, isActiveKitPending } = useBrandKits();
  const activeKitId = brandKitId ?? activeKit?.id ?? null;

  const query = useQuery<Omit<BestTimeSlotsResult, 'isLoading'>, Error>({
    // The active workspace is part of the key, not just the RPC arg — see
    // useAnalyticsDashboard.ts.
    queryKey: ['best-time-slots', user?.id ?? null, platform, activeKitId],
    queryFn: async () => {
      if (!user) return { ...SAFE_DEFAULTS };

      const { data, error } = await supabase.rpc('get_best_time', {
        p_user_id: user.id,
        p_platform: platform,
        p_brand_kit_id: activeKitId,
      });

      if (error) {
        logger.error('useBestTimeSlots: RPC error', error);
        throw new Error(error.message);
      }

      // get_best_time is a RETURNS TABLE function; Supabase returns an array.
      const row = BestTimeRowsSchema.parse(data ?? [])[0] ?? {};

      const tierRaw = row.tier;
      const tier = (tierRaw != null ? Number(tierRaw) : 0) as 0 | 1 | 2;

      return {
        slots: parseTopSlots(row.top_slots),
        tier,
        tierLabel: row.tier_label ?? '',
        n: row.n ?? 0,
        confidence: row.confidence ?? 'low',
        gated: Boolean(row.gated),
      };
    },
    enabled: !!user && !isActiveKitPending,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  return {
    ...(query.data ?? SAFE_DEFAULTS),
    isLoading: query.isLoading,
  };
}
